import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { IParamsApi } from '@/types/api/params.api';
import { Enum } from '@/constants';
import { ActionType, CriteriaType, IpRangeType, IPTypes, UserAgentBlockType } from '@/constants/enum';

export interface IItemBlock {
  id?: number;
  criteria: string | CriteriaType;
  linkRedirect?: string;
  shortUrl?: string;
  priority: string;
  country?: Array<string>;
  state?: Array<string>;
  city?: Array<string>;
  referUrl?: string;
  shortReferUrl?: string;
  ipAddress?: string;
  note?: string;
  ispName?: Array<string>;
  ispCode?: Array<string>;
  collectionId?: Array<string>;
  productId?: Array<string>;
  pageId?: Array<string>;
  type: string;
  deviceType?: string;
  osName?: string;
  browserName?: Array<string>;
  isActive: boolean;
  productName?: string[];
  collectionName?: string[];
  pageTitle?: string[];
  createdAt?: number;
  lastUpdatedAt?: number;
  overrideVpn?: number;
  ipRangeType?: IpRangeType;
  ipStart?: string;
  ipEnd?: string;
  inputIPMethod?: IPTypes;
  substring?: string;
  userAgentType?: UserAgentBlockType;
}
export interface IErrorRule {
  criteria?: CriteriaType;
  error?: string | React.ReactElement;
}
interface State {
  blackListTable: IParamsApi.IGetSettingList;
  whiteListTable: IParamsApi.IGetSettingList;
  isOpenGuide: boolean;
  inputSearchBlackList: string;
  inputSearchWhiteList: string;
  inputCity: string;
  inputIsp: string;
  inputProduct: string;
  inputCollection: string;
  inputSpecificPage: string;
  setting: IItemBlock;
  settingBackup: IItemBlock;
  tabSelected: number;
  tabSelectedModal: number;
  errorRule: IErrorRule[];
  errorCSV: string | React.ReactElement;
  fileDefault: File | null;
}

export const settingBockListInitial: IItemBlock = {
  isActive: true,
  overrideVpn: 1,
  type: ActionType.Block,
  criteria: '-1',
  id: undefined,
  linkRedirect: '',
  shortUrl: '',
  referUrl: '',
  shortReferUrl: '',
  note: '',
  priority: Enum.ListType.BlackList,
  collectionId: [],
  state: [],
  city: [],
  country: [],
  ipAddress: '',
  ispName: [],
  ispCode: [],
  productId: [],
  pageId: [],
  deviceType: '',
  osName: '',
  browserName: [],
  productName: [],
  ipRangeType: IpRangeType.CIDR,
  inputIPMethod: IPTypes.Manual,
  substring: '',
  userAgentType: UserAgentBlockType.DEVICE,
};

// Define the initial state using that type
export const initialState: State = {
  tabSelected: Enum.SelectedTab.Blacklist,
  tabSelectedModal: Enum.SelectedTabModal.Blacklist,
  blackListTable: {
    search: '',
    type: '',
    criteria: '',
    perPage: '10',
    page: 1,
    sort: Enum.SortType.DESC,
    sortBy: 'createdAt',
    priority: Enum.ListType.BlackList,
  },
  whiteListTable: {
    priority: Enum.ListType.WhiteList,
    search: '',
    criteria: '',
    perPage: '10',
    sort: Enum.SortType.DESC,
    sortBy: 'createdAt',
    page: 1,
  },
  setting: settingBockListInitial,
  settingBackup: settingBockListInitial,
  errorRule: [],
  errorCSV: '',
  isOpenGuide: false,
  inputSearchBlackList: '',
  inputSearchWhiteList: '',
  inputCity: '',
  inputIsp: '',
  inputProduct: '',
  inputCollection: '',
  inputSpecificPage: '',
  fileDefault: null,
};

export const blockListSlice = createSlice({
  name: 'blockList',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    handleTabSelected: (state, action: PayloadAction<number>) => {
      state.tabSelected = action.payload;
    },
    handleTabSelectedModal: (state, action: PayloadAction<number>) => {
      state.tabSelectedModal = action.payload;
    },
    handleBlackListTable: (state, action: PayloadAction<IParamsApi.IGetSettingList>) => {
      state.blackListTable = action.payload;
    },
    handleWhiteListTable: (state, action: PayloadAction<IParamsApi.IGetSettingList>) => {
      state.whiteListTable = action.payload;
    },
    handleOpenGuide: (state, action: PayloadAction<boolean>) => {
      state.isOpenGuide = action.payload;
    },
    handleInputSearchWhiteList: (state, action: PayloadAction<string>) => {
      state.inputSearchWhiteList = action.payload;
    },
    handleInputSearchBlackList: (state, action: PayloadAction<string>) => {
      state.inputSearchBlackList = action.payload;
    },
    handleInputCity: (state, action: PayloadAction<string>) => {
      state.inputCity = action.payload;
    },
    handleInputIsp: (state, action: PayloadAction<string>) => {
      state.inputIsp = action.payload;
    },
    handleInputProduct: (state, action: PayloadAction<string>) => {
      state.inputProduct = action.payload;
    },
    handleInputCollection: (state, action: PayloadAction<string>) => {
      state.inputCollection = action.payload;
    },
    handleInputSpecificPage: (state, action: PayloadAction<string>) => {
      state.inputSpecificPage = action.payload;
    },
    clearSetting: (state) => {
      state.setting = {
        ...initialState.setting,
      };
      state.settingBackup = {
        ...initialState.settingBackup,
      };
      state.inputCity = '';
      state.inputIsp = '';
      state.inputProduct = '';
      state.inputCollection = '';
      state.inputSpecificPage = '';
      state.blackListTable.page = 1;
      state.errorRule = [];
      state.errorCSV = '';
      state.fileDefault = null;
    },
    clearInputCity: (state) => {
      state.inputCity = '';
    },
    clearInputIsp: (state) => {
      state.inputIsp = '';
    },
    clearInputProduct: (state) => {
      state.inputProduct = '';
    },
    clearInputSpecificPage: (state) => {
      state.inputSpecificPage = '';
    },
    handleSetting: (state, action: PayloadAction<IItemBlock>) => {
      state.setting = action.payload;
    },
    handleSettingBackup: (state, action: PayloadAction<IItemBlock>) => {
      state.settingBackup = action.payload;
    },

    handleSettingEdit: (state, action: PayloadAction<IItemBlock>) => {
      state.settingBackup = action.payload;
      state.setting = action.payload;
      state.errorRule = [];
    },
    handleErrorRule: (state, action: PayloadAction<IErrorRule[]>) => {
      state.errorRule = action.payload;
    },
    handleErrorCSV: (state, action: PayloadAction<string | React.ReactElement>) => {
      state.errorCSV = action.payload;
    },
    handleChangeFileDefault: (state, action: PayloadAction<File | null>) => {
      state.fileDefault = action.payload;
    },
    handleChangeInputIPMethod: (state, action: PayloadAction<IPTypes>) => {
      state.setting.inputIPMethod = action.payload;
    },
  },
});

// Other code such as selectors can use the imported `RootState` type
export const blackListTableSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.blackListTable,
);
export const tabSelectedSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.tabSelected,
);
export const tabSelectedModalSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.tabSelectedModal,
);
export const settingSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.setting,
);

export const settingBackupSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.settingBackup,
);

export const whiteListTableSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.whiteListTable,
);
export const inputSearchBlackListSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.inputSearchBlackList,
);
export const inputSearchWhiteListSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.inputSearchWhiteList,
);

export const inputCitySelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.inputCity,
);

export const inputIspSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.inputIsp,
);

export const inputProductSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.inputProduct,
);

export const inputCollectionSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.inputCollection,
);

export const inputSpecificPageSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.inputSpecificPage,
);

export const openGuideSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.isOpenGuide,
);

export const errorRuleSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.errorRule,
);

export const errorCSVSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.errorCSV,
);

export const fileDefaultSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.fileDefault,
);

export const inputIPMethodSelector = createSelector(
  (state: RootState) => state.blockList,
  (state) => state.setting.inputIPMethod,
);

export const { handleSetting, handleErrorRule, handleErrorCSV } = blockListSlice.actions;

export default blockListSlice;
